import { createContext, useContext, useEffect, useState, useCallback, useRef, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@xstore/shared/api-client';
import type { AuthenticatedUser, TokenPair } from '@xstore/shared';

const ACCESS_TOKEN_KEY = 'xstore-access-token';
const REFRESH_TOKEN_KEY = 'xstore-refresh-token';
const REFRESH_INTERVAL_MS = 4 * 60 * 1000; // 4 minutes

interface TelegramLoginPayload {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

interface AuthContextValue {
  user: AuthenticatedUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  loginWithPassword: (email: string, password: string) => Promise<void>;
  loginWithTelegram: (payload: TelegramLoginPayload) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function getStoredTokens(): { accessToken: string | null; refreshToken: string | null } {
  return {
    accessToken: localStorage.getItem(ACCESS_TOKEN_KEY),
    refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY),
  };
}

function setStoredTokens(pair: TokenPair) {
  localStorage.setItem(ACCESS_TOKEN_KEY, pair.accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, pair.refreshToken);
}

function clearStoredTokens() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    const { refreshToken } = getStoredTokens();
    if (refreshToken == null) {
      clearStoredTokens();
      setUser(null);
      return;
    }

    try {
      const pair = await apiClient.post<TokenPair>('/auth/refresh', { refreshToken });
      setStoredTokens(pair);
    } catch {
      clearStoredTokens();
      setUser(null);
    }
  }, []);

  const bootstrap = useCallback(async () => {
    const { accessToken } = getStoredTokens();
    if (accessToken == null) {
      setIsLoading(false);
      return;
    }

    try {
      const me = await apiClient.get<AuthenticatedUser>('/auth/me');
      setUser(me);
    } catch {
      // Token invalid — try refresh once
      await refresh();
      const { accessToken: newToken } = getStoredTokens();
      if (newToken != null) {
        try {
          const me = await apiClient.get<AuthenticatedUser>('/auth/me');
          setUser(me);
        } catch {
          clearStoredTokens();
          setUser(null);
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, [refresh]);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  useEffect(() => {
    if (user == null) {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
      return;
    }

    refreshTimerRef.current = setInterval(() => {
      void refresh();
    }, REFRESH_INTERVAL_MS);

    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [user, refresh]);

  const loginWithPassword = useCallback(
    async (email: string, password: string) => {
      const pair = await apiClient.post<TokenPair>('/auth/login', { email, password });
      setStoredTokens(pair);
      const me = await apiClient.get<AuthenticatedUser>('/auth/me');
      setUser(me);
      void navigate('/');
    },
    [navigate],
  );

  const loginWithTelegram = useCallback(
    async (payload: TelegramLoginPayload) => {
      const pair = await apiClient.post<TokenPair>('/auth/telegram', payload);
      setStoredTokens(pair);
      const me = await apiClient.get<AuthenticatedUser>('/auth/me');
      setUser(me);
      void navigate('/');
    },
    [navigate],
  );

  const logout = useCallback(async () => {
    const { refreshToken } = getStoredTokens();
    if (refreshToken != null) {
      try {
        await apiClient.post('/auth/logout', { refreshToken });
      } catch {
        // ignore
      }
    }
    clearStoredTokens();
    setUser(null);
    void navigate('/login');
  }, [navigate]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: user != null,
        loginWithPassword,
        loginWithTelegram,
        logout,
        refresh,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (ctx == null) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
